import React from 'react';
import { IPublicMaterialPricing, IPublicPricing, IPublicRetailRange } from '../../services/pricing_public/pricing_public';
import { Slider } from './Controls.component';
import { money, StoreOrderEstimate } from './earnings';
import { ResultPanel, ResultRow } from './ResultPanel.component';

interface Props {
    pricing: IPublicPricing;
    material: IPublicMaterialPricing;
    range?: IPublicRetailRange;
    bounds: { min: number; max: number };
    retail: number;
    onRetail: (value: number) => void;
    estimate: StoreOrderEstimate;
    ordersPerMonth: number;
    /** The wall, material and orders controls shared with the licensing view. */
    sharedControls: React.ReactNode;
    columns: string;
}

// #region StoreProfitCalculator
/** "Your own store": a retail price in, what the brand keeps per order out, against Artist pricing. */
export const StoreProfitCalculator: React.FunctionComponent<Props> = ({ pricing, material, range, bounds, retail, onRetail, estimate, ordersPerMonth, sharedControls, columns }): React.ReactElement => {
    const fees = pricing.store_owner_fees;
    return (
        <div className={`grid gap-6 ${columns} sm:gap-8`}>
            <div className="flex flex-col gap-6">
                <Slider
                    id="pricing-retail"
                    label="Your retail price"
                    display={`${money(retail)}/sq ft`}
                    value={retail}
                    min={bounds.min}
                    max={bounds.max}
                    step={0.01}
                    onChange={onRetail}
                    hint={range
                        ? <>Stores on Wallmates charge {money(range.low)}–{money(range.high)} for {material.label}. {money(range.typical)} is typical.</>
                        : 'You set your own retail prices in Shopify.'}
                />
                {sharedControls}
            </div>

            <ResultPanel label="You'd keep" perOrder={estimate.profit} ordersPerMonth={ordersPerMonth}>
                <ResultRow term={`Customer pays · ${estimate.sqft} sq ft × ${money(retail)}`} value={money(estimate.revenue)} />
                <ResultRow term="Printing at Store Owner rates" value={`−${money(estimate.materialCost)}`} muted />
                <ResultRow term={`Fulfillment fee (${fees.fulfillment_fee_percent}% of standard value)`} value={`−${money(estimate.fulfillmentFee)}`} muted />
                <ResultRow term="Order processing" value={`−${money(estimate.orderProcessing)}`} muted />
                <ResultRow term="You keep" value={money(estimate.profit)} strong />
            </ResultPanel>
        </div>
    );
};
